import { ReactComponent as Refractotype } from '../../../assets/refraction/refractotype1.svg';

import { useState, useEffect } from 'react';
import { Select, Flex, Button, Text } from '../../../2022-component-library';
import { useTestContext } from '../../../context/testContext';
import { useCalibration } from '../../../context/calibrationContext';
import { ibisVisionToolOptions, calculateRefractionResult } from '../../../helpers/refraction';
import { useReceiveMessage, usePostMessage } from '../../../context/messageContext';
import styled from '@emotion/styled';

const Separator = styled(Flex)`
  background-color: #aab2b2;
  width: 1px;
  height: 90%;
`;
const refractionOptotypeOptions = [
  { label: 'Position 1', value: 1 },
  { label: 'Position 2', value: 2 },
  { label: 'Position 3', value: 3 },
];

const refractionStimulusSizeOptions = [
  { label: '10mm', value: '24' },
  { label: '20mm', value: '48' },
];

const ResponsiveRefractionTwoTestOptician = ({ eyeTested, setCanProgress }) => {
  const {
    calibrationData: { pixelDensity },
  } = useCalibration();

  const positionImages = {
    1: <Refractotype />,
    2: <Refractotype style={{ transform: 'rotate(60deg)' }} />,
    3: <Refractotype style={{ transform: 'rotate(-60deg)' }} />,
  };

  const { testData, updateTestData } = useTestContext();
  const postMessage = usePostMessage();

  const [stimulusBackground, setStimulusBackground] = useState(null);
  const [stimulusSize, setStimulusSize] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [visibleStimulus, setVisibleStimulus] = useState(true);
  const [positionData, setPositionData] = useState({
    1: {
      distance: null,
      tracking: false,
    },
    2: {
      distance: null,
      tracking: false,
    },
    3: {
      distance: null,
      tracking: false,
    },
  });

  const messageFromPatient = useReceiveMessage();

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const startDetections = (positionIndex) => {
    setPositionData({
      ...positionData,
      [positionIndex]: {
        ...positionData[positionIndex],
        tracking: true,
      },
    });

    postMessage({ type: 'detectDistance' });
  };

  const stopDetections = (positionIndex) => {
    postMessage({ type: 'stopDetections' });
    setPositionData({
      ...positionData,
      [positionIndex]: { ...positionData[positionIndex], tracking: false },
    });
  };

  const sendNewTarget = () => {
    postMessage({
      type: 'updateTarget',
      content: {
        stimulusBackground: 'white',
        stimulusSize: stimulusSize,
        stimulusOptotype: selectedPosition,
        visibleStimulus: visibleStimulus,
        pixelDensity: pixelDensity,
      },
    });
  };

  const distanceCollectionButtons = (positionIndex) => {
    return (
      <Flex flexDirection='column'>
        <Text fontSize='0.875rem' marginBottom='0.5rem' textAlign='center'>
          {`Position ${positionIndex}: (${positionData[positionIndex]?.distance || '--'}mm)`}
        </Text>
        <Flex flexDirection='row'>
          <Button
            disabled={Object.keys(positionData).find((element) => positionData[element].tracking) || positionIndex !== Number(selectedPosition)}
            onClick={() => {
              startDetections(positionIndex);
            }}
            width='45%'
            margin='0px 5px'
          >
            Track
          </Button>
          <Button
            disabled={!positionData[positionIndex].tracking}
            onClick={() => {
              stopDetections(positionIndex);
            }}
            width='45%'
            margin='0px 5px'
          >
            Capture
          </Button>
        </Flex>
      </Flex>
    );
  };

  useEffect(() => {
    if (messageFromPatient.type === 'patientDistance') {
      for (let i = 1; i <= 3; i++) {
        if (positionData[i].tracking === true) {
          setPositionData({
            ...positionData,
            [i]: {
              ...positionData[i],
              distance: messageFromPatient.content,
            },
          });
        }
      }
    }
  }, [messageFromPatient]);

  useEffect(() => {
    if (stimulusBackground === null && stimulusSize === null) {
      postMessage({ type: 'initialiseDetection', content: ibisVisionToolOptions[0].values });
      setStimulusBackground('white');
      setStimulusSize('24');
      setSelectedPosition(1);
    } else {
      sendNewTarget();
    }
  }, [stimulusBackground, stimulusSize, selectedPosition, visibleStimulus]);

  useEffect(() => {
    if (positionData[1].distance !== null && positionData[2].distance !== null && positionData[3].distance !== null) {
      setCanProgress(true);
      const results = calculateRefractionResult(positionData[1].distance, positionData[2].distance, positionData[3].distance);
      updateTestData({ [eyeTested]: { ...testData[eyeTested], ...{ score: results } } });
    } else {
      setCanProgress(false);
    }
  }, [positionData, eyeTested, updateTestData]);

  return (
    <Flex column alignItems='center' padding='1.5rem 1rem 0 1rem' width='90vw' maxWidth='720px'>
      <Flex justifyContent='space-between' width='90%' marginBottom='1rem'>
        <Flex flexDirection='column' width='50%' alignItems='center'>
          <Select
            label='Optotype'
            value={selectedPosition}
            onChange={(e) => {
              setSelectedPosition(e.target.value);
            }}
            width='100%'
          >
            {refractionOptotypeOptions.map((opt) => {
              return (
                <option key={`test-option-${opt.value}`} value={opt.value}>
                  {opt.label}
                </option>
              );
            })}
          </Select>
          <Select
            label='Stimulus Size'
            value={stimulusSize}
            onChange={(e) => {
              setStimulusSize(e.target.value);
            }}
            width='100%'
          >
            {refractionStimulusSizeOptions.map((stimulusSize) => {
              return (
                <option key={`test-option-${stimulusSize.value}`} value={stimulusSize.value}>
                  {stimulusSize.label}
                </option>
              );
            })}
          </Select>
          <Select
            label='Display Stimulus'
            value={visibleStimulus ? 'yes' : 'no'}
            width='100%'
            onChange={(e) => {
              setVisibleStimulus(e.target.value === 'yes' ? true : false);
            }}
          >
            {[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ].map((type) => {
              return <option value={type.value}>{type.label}</option>;
            })}
          </Select>
        </Flex>
        <Flex
          column
          centered
          border='1px solid black'
          borderRadius='4px'
          width='30%'
          height='45vh'
          maxHeight='250px'
          maxWidth='350px'
          padding='0.5rem'
        >
          <Text fontSize='0.75rem'>Preview</Text>
          <Flex
            marginTop='0.5rem'
            justifyContent='center'
            style={{ border: '1px solid black', position: 'relative', backgroundColor: 'black' }}
            height='60%'
            width='100%'
          >
            <Flex
              style={{
                display: visibleStimulus ? 'block' : 'none',
                position: 'absolute',
                backgroundColor: 'transparent',
                borderRadius: '50%',
                width: '60%',
                height: '80px',
              }}
            >
              {positionImages[selectedPosition]}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Flex height='15vh' maxHeight='75px' centered marginBottom='1rem' maxWidth='95vw'>
        {distanceCollectionButtons(1)}
        <Separator />
        {distanceCollectionButtons(2)}
        <Separator />
        {distanceCollectionButtons(3)}
      </Flex>
    </Flex>
  );
};

export default ResponsiveRefractionTwoTestOptician;
